import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { Plus, SquarePen, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getAllRoles, deleteRole } from '../Services/RolesPermissions/RolesPermissionsApi';
import PermissionWrapper from '../Components/Auth/PermissionWrapper';
import DeleteModal from '../Components/Modals/DeleteModal';
import { useGlobalSnackbar } from '../Components/Common/GlobalSanckbar';

const RolesPermissionsManagement = () => {
    const navigate = useNavigate();
    const { showSnackbar } = useGlobalSnackbar();
    const [rolesData, setRolesData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Delete modal state
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const fetchRolesData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getAllRoles();

            if (response && response.success && response.data) {
                setRolesData(response.data);
            } else {
                setRolesData([]);
            }
        } catch (err) {
            console.error(err);
            setError("Failed to fetch roles. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRolesData();
    }, [fetchRolesData]);

    const handleDeleteClick = (id) => {
        setSelectedId(id);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (selectedId) {
            try {
                setIsDeleting(true);
                setDeleteError(null);
                const response = await deleteRole(selectedId);
                if (response && response.success) {
                    setDeleteModalOpen(false);
                    setSelectedId(null);
                    showSnackbar(response?.message || "Role deleted successfully!", "success");
                    fetchRolesData(); // Refresh the list
                } else {
                    const errMsg = response?.message || "Failed to delete role.";
                    setDeleteError(errMsg);
                    showSnackbar(errMsg, "error");
                }
            } catch (err) {
                console.error("Delete Error:", err);
                const errorMessage = err.response?.data?.message || err.message || "Failed to delete role.";
                setDeleteError(errorMessage);
                showSnackbar(errorMessage, "error");
            } finally {
                setIsDeleting(false);
            }
        }
    };

    const handleAdd = () => {
        navigate('/roles-permissions/add');
    };

    const handleEdit = (role) => {
        navigate(`/roles-permissions/edit/${role.id}`, { state: { role } });
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Typography sx={{
                        color: '#222',
                        fontSize: { xs: '1.75rem', md: '1.8rem' },
                        fontWeight: 500
                    }}>
                        Roles & Permissions
                    </Typography>
                    <PermissionWrapper permission="create" mode="disable">
                        <Button
                            variant="contained"
                            startIcon={<Plus size={18} />}
                            onClick={handleAdd}
                            sx={{
                                bgcolor: '#FFBD65',
                                color: '#fff',
                                textTransform: 'none',
                                borderRadius: '6px',
                                px: 2.5,
                                height: '40px',
                                fontSize: '0.85rem'
                            }}
                        >
                            Add Role
                        </Button>
                    </PermissionWrapper>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ width: '100%' }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1, whiteSpace: 'nowrap', textAlign: 'center', width: '4%' }}>S.No</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, whiteSpace: 'nowrap', width: '15%' }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, width: '23%' }}>Description</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, whiteSpace: 'nowrap', width: '8%' }}>Active</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, whiteSpace: 'nowrap', width: '12%' }}>Created By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, whiteSpace: 'nowrap', width: '11%' }}>Created At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, whiteSpace: 'nowrap', width: '12%' }}>Updated By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, whiteSpace: 'nowrap', width: '10%' }}>Updated At</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1, whiteSpace: 'nowrap', width: '5%' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rolesData.length > 0 ? (
                                    rolesData.map((role, idx) => (
                                        <TableRow key={role.id} sx={{
                                            transition: 'background-color 0.2s',
                                            '& td': { borderBottom: '1px solid #F0F0F0' },
                                            '&:last-child td': { borderBottom: 0 },
                                        }}>
                                            <TableCell sx={{ py: 1.25, px: 1, fontSize: '0.75rem', color: '#64748B', textAlign: 'center' }}>{idx + 1}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.25, maxWidth: 150 }}>
                                                <Typography sx={{ fontSize: '0.75rem', color: '#1E293B', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={role.name}>
                                                    {role.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.25, maxWidth: 220 }}>
                                                <Typography sx={{ fontSize: '0.75rem', color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={role.description}>
                                                    {role.description || '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.25, whiteSpace: 'nowrap' }}>
                                                <Box
                                                    sx={{
                                                        display: 'inline-block',
                                                        px: 1.25, py: 0.35,
                                                        borderRadius: '4px',
                                                        bgcolor: role.isActive ? '#E8F5E8' : '#FFEBEE',
                                                        color: role.isActive ? '#2E7D32' : '#C62828',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    {role.isActive ? 'Active' : 'Inactive'}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.25, maxWidth: 120 }}>
                                                {role.cuName ? (
                                                    <Typography sx={{ fontSize: '0.75rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={role.cuName}>
                                                        {role.cuName}
                                                        {role.cuType ? (
                                                            <Typography component="span" sx={{ fontSize: '0.7rem', color: '#888', ml: 0.5 }}>
                                                                ({role.cuType})
                                                            </Typography>
                                                        ) : null}
                                                    </Typography>
                                                ) : <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>-</Typography>}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.25, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(role.createdAt)}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.25, maxWidth: 120 }}>
                                                {role.uuName ? (
                                                    <Typography sx={{ fontSize: '0.75rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={role.uuName}>
                                                        {role.uuName}
                                                        {role.uuType ? (
                                                            <Typography component="span" sx={{ fontSize: '0.7rem', color: '#888', ml: 0.5 }}>
                                                                ({role.uuType})
                                                            </Typography>
                                                        ) : null}
                                                    </Typography>
                                                ) : <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>-</Typography>}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.25, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(role.updatedAt)}
                                            </TableCell>

                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5 }}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <PermissionWrapper permission="edit" mode="disable">
                                                        <Tooltip title="Edit">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                                onClick={() => handleEdit(role)}
                                                                sx={{ color: '#FFBD65' }}
                                                                size="small"
                                                            >
                                                                <SquarePen size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                    <PermissionWrapper permission="delete" mode="disable">
                                                        <Tooltip title="Delete">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                                onClick={() => handleDeleteClick(role.id)}
                                                                sx={{ color: '#FF4D49' }}
                                                                size="small"
                                                            >
                                                                <Trash2 size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#AAA', fontSize: '0.8rem' }}>
                                            No roles found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
                <DeleteModal
                    open={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    onDelete={handleConfirmDelete}
                    title="Delete Role"
                    description="Are you sure you want to delete this role? This action cannot be undone."
                    error={deleteError}
                    loading={isDeleting}
                />
            </Box>
        </PermissionWrapper>
    );
};

export default RolesPermissionsManagement;
